/*!
 * Omneo Lists — Cards Module (Phase 2a: Wish List, Phase 2b: Reserve
 * List, Phase 2c: Recommended For You List, Phase 2d: Move actions)
 * -------------------------------------------------------------------------
 * Renders all three list grids on the lists page (/pages/list),
 * replacing the legacy AJAX-fetch-per-item pattern (one
 * /search?view=your-list request per saved item) with one
 * /products/{handle}.js request per saved item.
 *
 * Wish List: works for both logged-in and guest customers (session
 * cookie only for guests). Reserve List and Recommended For You:
 * logged-in only, per business rules — no guest path needed.
 *
 * Omneo is used to know WHICH items are on each list (handle, sku) plus
 * list-specific metadata Shopify doesn't have (omneoItemId, createdAt,
 * and for Reserve List, status). The actual card data (price, image,
 * variant, size, availability) comes from Shopify's own product JSON.
 *
 * Badge, sort-order, status, and action-link logic is ported line-for-
 * line from templates/search.your-list.liquid (the actual live-fetched
 * legacy template) — that file is the source of truth, not a fresh
 * design.
 *
 * REMAINING KNOWN GAPS (by design — later work):
 * - Zapier webhook on reserve-add not migrated — separate follow-up,
 *   per earlier discussion (rewire onto sync:success/sync:failed).
 * - Share My List page untouched.
 * - Move actions use a plain browser alert() for the one-size-per-
 *   reserve-list block message — functional, not polished.
 *
 * Requires: window.jQuery, window.OmneoLists (omneo-lists.js, loaded
 * first). Self-guards on document.getElementById('wish-list-section')
 * — does nothing if that element isn't on the page (all three sections
 * always co-occur on the real lists page template).
 * -------------------------------------------------------------------------
 */
(function ($, OmneoLists) {
  'use strict';

  if (!$ || !OmneoLists) return;

  // Found via investigation, not testing — this page was never in scope
  // for Phase 2 and self-guarding on #wish-list-section existing alone
  // isn't enough to exclude it, since it reuses the same section ID.
  // Share My List already has two competing legacy renderers targeting
  // #wish-list-section: a SKU-from-URL one (correct — shows the
  // sender's shared items, theme.js's ViewedList/moveAlong()) and an
  // Omneo-Shapes-SDK-driven one (incorrect for this page — shows the
  // *visitor's own* wishlist, omneo-lists.liquid's processListItem for
  // template-page-share-my-list, deliberately left untouched when we
  // migrated the main list page). Without this guard, this module would
  // be a third competing renderer here, also showing the visitor's own
  // data rather than the shared SKUs. Excluded entirely until this page
  // gets its own properly-scoped fix.
  if (document.body.classList.contains('template-page-share-my-list')) return;

  var $wishSection = document.getElementById('wish-list-section');
  if (!$wishSection) return; // not on the lists page — nothing to do

  var $reserveSection = document.getElementById('reserve-list-section');

  // ---------------------------------------------------------------------
  // Badge computation — ported from search.your-list.liquid's
  // `listProductLabel` capture block (lines ~102-132 of that file).
  // Order matters: first match wins, exactly like the {% elsif %} chain.
  // ---------------------------------------------------------------------
  function computeBadge(card) {
    // card fields used: templateSuffix, tags, inventoryQuantity,
    // available, compareAtPrice, price

    if (card.available && card.templateSuffix === 'pre-order') {
      return { label: 'Pre Order', cssClass: 'in-stock-label', sortOrder: 4 };
    }
    if (card.tags.indexOf('coming-soon') !== -1) {
      return { label: 'Coming Soon', cssClass: 'coming-soon-label', sortOrder: 5 };
    }
    if (card.inventoryQuantity === 1) {
      return { label: 'One Left', cssClass: 'one-left-label', sortOrder: 1 };
    }
    if (card.inventoryQuantity === 2) {
      return { label: 'Nearly Gone', cssClass: 'nearly-gone-label', sortOrder: 2, small: '! Only 2 left in your size' };
    }
    if (card.compareAtPrice > card.price && card.available) {
      return { label: 'Sale', cssClass: 'on-sale-label', sortOrder: 3 };
    }
    if (card.inventoryQuantity > 2) {
      if (card.tags.indexOf('manual-list-lock') !== -1) {
        return { label: '', cssClass: '', sortOrder: 4 }; // In Stock label suppressed, per {% unless %}
      }
      return { label: 'In Stock', cssClass: 'in-stock-label', sortOrder: 4 };
    }
    if (card.tags.indexOf('restock') !== -1 && !card.available) {
      return { label: 'Back Soon', cssClass: 'back-soon-label', sortOrder: 6 };
    }
    if (!card.available) {
      return { label: 'Sold out', cssClass: 'sold-out-label', sortOrder: 7 };
    }
    return { label: '', cssClass: '', sortOrder: 4 };
  }

  // ---------------------------------------------------------------------
  // Launch/restock date tag parsing — ported from the same file's
  // sortOrderSecondary + launchdate/reservedItemStatus blocks.
  // Tags look like "launch-2026-08-26" or "restock-2026-08-26".
  // ---------------------------------------------------------------------
  function parseLaunchInfo(tags) {
    var launchTag = tags.filter(function (t) {
      return t.indexOf('launch-') === 0 || t.indexOf('restock-') === 0;
    })[0];

    if (!launchTag) return { launchDate: null, sortOrderSecondary: 0, closed: false, daysToLaunch: null };

    var dateStr = launchTag.replace('launch-', '').replace('restock-', '');
    var launchDate = new Date(dateStr + 'T00:00:00Z'); // ASSUMPTION: UTC, same caveat as parseOmneoDate elsewhere
    if (isNaN(launchDate.getTime())) return { launchDate: null, sortOrderSecondary: 0, closed: false, daysToLaunch: null };

    var sortOrderSecondary = parseInt(dateStr.replace(/-/g, ''), 10) || 0;
    var now = new Date();
    var diffDays = Math.floor((launchDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    // "closed" = within 7 days of launch — matches reservedItemStatus logic
    var closed = diffDays < 7;

    return { launchDate: launchDate, sortOrderSecondary: sortOrderSecondary, closed: closed, daysToLaunch: diffDays };
  }

  // ---------------------------------------------------------------------
  // Wish List status — ported from omneo-lists.liquid's per-item status
  // computation (lines ~442-460).
  //
  // BUG FOUND AND FIXED, NOT REPLICATED: the legacy code declared
  // `productRestock` with `const` both outside and inside an `if` block.
  // The inner declaration shadows the outer within its own block scope,
  // so the outer variable is never actually set to true — "Re Stock"
  // status was dead code; every item older than 6 months showed
  // "Expired" regardless of the restock tag. Fixed here to honor the
  // obvious intent.
  //
  // Guests have no createdAt to check — the session cookie only stores
  // handle:productId:variantId, no timestamp, and cookies expire after
  // 7 days anyway (see omneo-lists.js DEFAULTS.cookieExpiryDays) — a
  // guest item can never actually reach the 6-month threshold. Guest
  // items are always "Current".
  // ---------------------------------------------------------------------
  var WISHLIST_EXPIRY_MONTHS = 6;

  function computeWishlistStatus(card) {
    if (!card.createdAt) return 'Current'; // guest path — see comment above

    var sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - WISHLIST_EXPIRY_MONTHS);
    var olderThanSixMonths = card.createdAt < sixMonthsAgo;

    if (!olderThanSixMonths) return 'Current';
    return card.tags.indexOf('restock') !== -1 ? 'Re Stock' : 'Expired';
  }

  // ---------------------------------------------------------------------
  // Data normalization — both paths (Omneo / guest) converge here into
  // one common shape before badge/sort/card-building runs.
  // ---------------------------------------------------------------------
  // Same fix as Phase 1's getCleanProductHandle() in omneo-lists-ui.js —
  // Omneo's product.handle includes an external_id suffix
  // ("...citrus-6122") that isn't part of the real Shopify handle.
  // Duplicated here (small, stable utility) rather than reaching into
  // the other file's closure — kept in sync manually, same logic.
  function getCleanProductHandle(product) {
    if (!product || !product.handle) return null;
    var externalId = product.external_id;
    if (!externalId) return product.handle;
    var suffix = '-' + externalId;
    return product.handle.slice(-suffix.length) === suffix
      ? product.handle.slice(0, -suffix.length)
      : product.handle;
  }

  function normalizeOmneoItem(item) {
    var variant = item.product_variant || {};
    var product = item.product || {};
    var handle = getCleanProductHandle(product);
    return {
      omneoItemId: item.id,
      handle: handle,
      sku: variant.sku,
      createdAt: item.created_at ? new Date(item.created_at.replace(' ', 'T') + 'Z') : null
    };
  }

  // ---------------------------------------------------------------------
  // Unified product fetch — used for BOTH logged-in and guest items.
  //
  // ARCHITECTURE CHANGE from the original Phase 2a plan: this used to
  // build logged-in cards straight from the Omneo payload with zero
  // extra requests, and only fetch /products/{handle}.js for guests.
  // Changed after staging feedback surfaced two real requirements the
  // Omneo payload can't satisfy on its own:
  //   1. Add to Cart needs a real Shopify variant ID — Omneo's own
  //      variant "id"/"external_id" are Omneo-internal, not Shopify's.
  //   2. Size display (also missing before this) needs the real
  //      Shopify variant's option value.
  // Both logged-in and guest cards now resolve through the same
  // /products/{handle}.js fetch — one request per saved item either
  // way. Omneo is still used to know WHICH items are on the list
  // (handle, sku) plus list-specific metadata (omneoItemId, createdAt)
  // that Shopify's product data doesn't have.
  // ---------------------------------------------------------------------
  function buildCardFromProductJson(productJson, match, extra) {
    var variants = productJson.variants || [];
    var variant = null;
    if (match.variantId) {
      variant = variants.filter(function (v) { return String(v.id) === String(match.variantId); })[0];
    } else if (match.sku) {
      variant = variants.filter(function (v) { return v.sku === match.sku; })[0];
    }
    variant = variant || variants[0];

    return {
      omneoItemId: extra.omneoItemId || null,
      productId: productJson.id,
      variantId: variant ? variant.id : null,
      handle: productJson.handle,
      sku: variant ? variant.sku : sku,
      title: productJson.title,
      // Matches the confirmed-live Liquid template's convention
      // (product.selected_or_first_available_variant.option2) — size is
      // option2 in this theme (option1 is baked into the product/handle
      // as colour, since each colourway is its own product listing).
      size: variant ? variant.option2 : null,
      price: variant ? variant.price / 100 : null, // /products/{handle}.js prices are in cents
      compareAtPrice: variant && variant.compare_at_price > variant.price ? variant.compare_at_price / 100 : null,
      image: variant && variant.featured_image ? variant.featured_image.src : (productJson.featured_image || null),
      tags: (productJson.tags || []).map(function (t) { return t.toLowerCase(); }),
      available: variant ? variant.available : false,
      // ⚠ ASSUMPTION, not yet verified on staging: /products/{handle}.js
      // includes variants[].inventory_quantity in this theme's setup.
      // If it doesn't, "One Left"/"Nearly Gone" badges silently won't
      // trigger (available stays accurate either way).
      inventoryQuantity: variant ? variant.inventory_quantity : null,
      templateSuffix: productJson.template_suffix || null,
      createdAt: extra.createdAt || null,
      url: '/products/' + productJson.handle
    };
  }

  function fetchProductJson(handle) {
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) {
          var err = new Error('Product fetch failed: ' + res.status + ' for ' + handle);
          // Attached so callers can distinguish an expected 404
          // (product genuinely discontinued/hidden — happens on every
          // load for old items, not actionable) from anything else
          // (network failure, 500, timeout — genuinely worth logging).
          // A network-level failure (fetch() itself rejecting, never
          // reaching this .then()) has no .status at all, which
          // callers should treat as "not a plain 404" and still log.
          err.status = res.status;
          throw err;
        }
        return res.json();
      });
  }

  // A 404 here means the product is genuinely gone (discontinued/
  // unpublished) — expected, happens on every load for old saved
  // items, not a bug and not actionable. Anything else (a different
  // status code, or no .status at all — a network-level failure that
  // never reached fetchProductJson's .then()) is a genuine, unexpected
  // failure worth surfacing. Found via a real report: logging every
  // 404 as a "Render Failed" would flood Zapier with noise for a
  // common, harmless, expected case.
  function isExpectedMissingProduct(err) {
    return err && err.status === 404;
  }

  // ---------------------------------------------------------------------
  // SKU-based fallback for stale Omneo handles.
  //
  // Found and confirmed via a real live issue: an item genuinely
  // reserved and Allocated in Omneo wasn't showing on the Reserve List
  // page. Traced to Omneo's stored product.handle being stale — the
  // product had been renamed in Shopify (a mid-collection name change,
  // "fr" → "lettuce") and Omneo's copy never picked up the update.
  // /products/{the-stale-handle}.js correctly 404'd — not a bug, Omneo
  // was just telling us the wrong handle.
  //
  // SKU is a far more stable identifier than handle — it's tied to
  // inventory/fulfillment, not to how a product happens to be named
  // right now. This uses Shopify's Predictive Search API
  // (/search/suggest.json), confirmed via live testing to correctly
  // resolve a SKU to its current, correct product — including the
  // handle — even when the handle we already had was wrong.
  //
  // Two-step approach rather than building cards directly from the
  // search response: that response is missing several fields we need
  // (no inventory_quantity, no per-variant sku for exact-match
  // verification, size embedded in a combined string rather than a
  // separate field) and uses different shapes for the ones it does
  // have. Simpler and more reliable to use the search result only to
  // recover the CORRECT handle, then feed that into our existing,
  // already-proven fetchProductJson() — same trusted data shape as
  // every other card on every list, no new parsing logic needed.
  //
  // ⚠ The search response doesn't include variant.sku, even though the
  // search itself queried on that field — so there's no way to confirm
  // an EXACT match from the search response alone. Verified instead
  // after the fact: once we have full product data (which DOES include
  // real variant SKUs), we check that at least one variant genuinely
  // matches. If not, we don't use the result — safer to fail the item
  // entirely than risk silently substituting the wrong product.
  function fetchProductJsonBySku(sku) {
    if (!sku) return Promise.reject(new Error('No SKU available to search with'));

    var url = '/search/suggest.json?q=' + encodeURIComponent(sku) +
      '&resources[type]=product&resources[options][fields]=variants.sku' +
      '&resources[options][unavailable_products]=show';

    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('SKU search failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        if (!products.length) throw new Error('SKU search found no results for ' + sku);

        var candidateHandle = products[0].handle;
        return fetchProductJson(candidateHandle).then(function (productJson) {
          var hasExactMatch = (productJson.variants || []).some(function (v) { return v.sku === sku; });
          if (!hasExactMatch) {
            throw new Error('SKU search result for "' + sku + '" did not contain an exact variant match — not using it.');
          }
          return productJson;
        });
      });
  }

  // Tries the primary handle first (fast, correct the vast majority of
  // the time); on a plain 404 specifically, falls back to the SKU
  // search above rather than giving up immediately. A successful
  // recovery is reported (not silently swallowed) — the underlying
  // Omneo data is still wrong and should be fixed at the source, even
  // though the customer-facing result is now correct. Any OTHER error
  // (network failure, non-404 status, or the SKU fallback also failing)
  // propagates normally, same as before this existed.
  function fetchProductJsonWithFallback(type, handle, sku) {
    return fetchProductJson(handle).catch(function (primaryErr) {
      if (!isExpectedMissingProduct(primaryErr) || !sku) throw primaryErr;

      return fetchProductJsonBySku(sku).then(function (productJson) {
        console.warn('[OmneoLists Cards] Recovered via SKU fallback — Omneo\'s stored handle ("' +
          handle + '") was stale/incorrect for SKU ' + sku + '. Correct handle: "' + productJson.handle + '".');
        OmneoLists.reportRenderFailure(type, handle,
          new Error('Recovered via SKU fallback, but Omneo\'s stored handle is stale and should be corrected at the source. ' +
            'Stale handle: "' + handle + '". Correct handle: "' + productJson.handle + '".'));
        return productJson;
      });
      // If the SKU fallback ALSO fails, its rejection propagates as-is
      // — caller's existing 404-vs-other-error handling applies
      // unchanged (a fallback failure isn't itself guaranteed to be a
      // 404, so isExpectedMissingProduct() naturally does the right
      // thing either way).
    });
  }

  // ---------------------------------------------------------------------
  // Card markup — structure matches search.your-list.liquid's card div
  // closely enough to inherit existing CSS, trimmed to what Phase 2a
  // (Wish List only) needs. Reserve-specific overlays (reserved-label,
  // reserve-allocated, etc.) and reserve/recommended move-actions are
  // intentionally NOT included here — Phase 2b/2c territory.
  // ---------------------------------------------------------------------
  function money(amount) {
    if (amount === null || amount === undefined) return '';
    // ⚠ ASSUMPTION: simple currency formatting, not using Shopify's
    // `money_with_currency` filter (not available client-side). Check
    // against the store's actual currency/locale formatting on staging
    // — this assumes NZD-style "$123.45", matching what's been seen in
    // this store's data throughout testing today.
    return '$' + Number(amount).toFixed(2);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  // ---------------------------------------------------------------------
  // Action links — shared across all three card builders. Move actions
  // ported from the legacy card template's prod-item-actions block:
  //   reserved-to-wishlist        (Reserve List cards)
  //   wishlist-to-reserve-list    (Wish List cards, level-3-plus gated)
  //   recommended-to-wishlist     (Recommended cards)
  //   recommended-to-reserve-list (Recommended cards, level-3-plus gated)
  // "level-3-plus" class visibility is fully CSS-driven off the same
  // body tier classes Phase 1's applyProfileClasses() already sets
  // (confirmed in custom.css ~line 3251) — no extra JS needed here.
  // Remove is common to all three list types.
  // ---------------------------------------------------------------------
  function buildActionLinksHtml(card, listType, launchInfo) {
    var attrs = ' data-producthandle="' + escapeHtml(card.handle) +
      '" data-productid="' + card.productId + '" data-productvariantid="' + (card.variantId || '') +
      '" data-productsku="' + escapeHtml(card.sku) + '" data-producttitle="' + escapeHtml(card.title) + '"';

    // Confirmed via the legacy template (search.your-list.liquid,
    // ~lines 262-278): "Move to reserve list" only shows when the
    // product is tagged coming-soon AND not within the 7-days-before-
    // launch "closed" window. Reserve List is for requesting a product
    // ahead of/at launch — once something's already generally
    // available, there's no reason to "reserve" it. Missed this
    // initially; caught via testing.
    //
    // ALSO explicitly gated on login state, not just the level-3-plus
    // CSS class — Reserve List requires being logged in at all (tier
    // classes only exist for logged-in users via Phase 1's
    // applyProfileClasses(), so CSS *should* already hide this for
    // guests), but relying on CSS alone for a business rule this
    // important isn't solid enough. Found showing for guests via
    // testing — fixed by checking isLoggedIn directly here too.
    //
    // Tier itself is deliberately left to CSS alone (level-3-plus,
    // gated on the cll-{tier} body classes) — a JS-level tier snapshot
    // was tried and reverted: it's taken once at render time, and if
    // the tier class hasn't been set on body yet at that exact moment
    // (a realistic timing risk given this SDK's demonstrated event-
    // timing quirks elsewhere in this project), an eligible L3/L4/Team
    // Fave customer would permanently never see the link — baked into
    // the rendered HTML string with no way to self-correct once the
    // class does arrive. CSS doesn't have that problem; it re-evaluates
    // continuously regardless of when the class gets added. The actual
    // "showing for Level 1" bug traced to a different root cause — see
    // applyProfileClasses() in omneo-lists.js.
    var isLoggedIn = OmneoLists._getConfig().isLoggedIn;
    var isComingSoon = card.tags.indexOf('coming-soon') !== -1;
    var canMoveToReserve = isLoggedIn && !launchInfo.closed && isComingSoon;
    var canMoveFromReserve = !launchInfo.closed;

    var links = '';
    if (listType === 'reserve_list' && canMoveFromReserve) {
      links += '<span class="action-link" data-action="reserved-to-wishlist"' + attrs + '><span class="down-arrow">&darr;</span> Move to wishlist</span>';
    }
    if (listType === 'wish_list' && canMoveToReserve) {
      links += '<span class="action-link level-3-plus" data-action="wishlist-to-reserve-list"' + attrs + '><span class="up-arrow">&uarr;</span> Move to reserve list</span>';
    }
    if (listType === 'recommended_list') {
      links += '<span class="action-link" data-action="recommended-to-wishlist"' + attrs + '><span class="up-arrow">&uarr;</span> Move to wishlist</span>';
      if (canMoveToReserve) {
        links += '<span class="action-link level-3-plus" data-action="recommended-to-reserve-list"' + attrs + '><span class="up-arrow">&uarr;</span> Move to reserve list</span>';
      }
    }
    links += '<span class="action-link" data-action="remove-list-item"' + attrs + '><span class="cross">&#x2715;</span> Remove</span>';
    return links;
  }

  function buildCardHtml(card) {
    var badge = computeBadge(card);
    var launchInfo = parseLaunchInfo(card.tags);
    var status = computeWishlistStatus(card);

    // Split "Title (Fabric) Colour" the same way the Liquid template does
    var title = card.title;
    var colour = '';
    var fabric = '';
    if (card.title && card.title.indexOf('(') !== -1) {
      title = card.title.split('(')[0];
      var afterOpen = card.title.split('(').slice(1).join('(');
      fabric = afterOpen.split(')')[0];
      colour = afterOpen.split(')').slice(1).join(')');
    }

    var priceHtml = card.compareAtPrice
      ? '<span style="text-decoration: line-through; opacity: 0.5;">' + money(card.compareAtPrice) + '</span> ' + money(card.price)
      : money(card.price);

    var badgeHtml = badge.label ? '<label class="' + badge.cssClass + ' type-wishlist">' + badge.label + '</label>' : '';
    var messagingHtml = badge.small ? '<div class="product-status-grid">' + badge.small + '</div>' : '';

    var launchHtml = '';
    if (launchInfo.launchDate) {
      var launchLabel = launchInfo.launchDate.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' }).toUpperCase();
      launchHtml = '<div class="online-date"><span class="label">Available</span> ' + launchLabel + '</div>';
    }

    var idAttr = card.omneoItemId
      ? 'omneo-' + card.omneoItemId
      : card.productId + '-' + (card.variantId || '');
    var itemIdAttr = card.omneoItemId ? ' data-omneo-item-id="' + card.omneoItemId + '"' : '';

    var sizeHtml = card.size
      ? '<div class="list-prod-size list-column">Size - <span class="large">' + escapeHtml(card.size) + '</span></div>'
      : '';

    // Add to Cart — ported from search.your-list.liquid's action block.
    // CORRECTED per business decision, confirmed 2026-07-15: the
    // original legacy logic (and our initial port of it) let a
    // coming-soon product bypass the lock if tagged 'early-access' —
    // traced to main-product.liquid/search.your-list.liquid and
    // confirmed this was ALWAYS a pure product-tag check, never tied to
    // customer eligibility/tier. That meant ANY visitor could buy an
    // early-access-tagged coming-soon item straight from their Wish
    // List, regardless of who they are. Reported live as "early access
    // products are showing on Wish List" — confirmed as unwanted, not a
    // porting bug (the port was faithful to the original), and fixed
    // here by removing the exception: coming-soon now always blocks Add
    // to Cart on the Wish List, full stop. Scoped to Wish List only per
    // request — Reserve List and Recommended still use the original
    // (early-access-exempt) logic, since only Wish List was flagged.
    var isComingSoon = card.tags.indexOf('coming-soon') !== -1;
    var isLocked = card.tags.indexOf('manual-list-lock') !== -1;
    var showAddToCart = card.available && !isLocked && !isComingSoon;

    var addToCartHtml = '';
    if (showAddToCart && card.variantId) {
      addToCartHtml =
        '<div class="wishlist-form-add-to-cart-wrap">' +
          '<input type="button" class="AddtoCartListButton button" ' +
            'data-producthandle="' + escapeHtml(card.handle) + '" data-productid="' + card.productId +
            '" data-productvariantid="' + card.variantId + '" value="Add to Cart" />' +
        '</div>';
    }

    return (
      '<div class="product-index wishlist-list-item span-3 sm-span-12 auto session-item">' +
      '<div id="' + idAttr + '" class="product-index-inner status-' + status.replace(' ', '-') + '"' +
        ' data-handle="' + escapeHtml(card.handle) + '" data-sku="' + escapeHtml(card.sku) + '"' +
        ' data-sortorder="' + badge.sortOrder + '" data-sortordersecondary="' + launchInfo.sortOrderSecondary + '"' +
        itemIdAttr + '>' +
        '<div class="js-product-image prod-image">' +
          '<a href="' + escapeHtml(card.url) + '" title="' + escapeHtml(card.title) + '">' +
            (card.image ? '<img src="' + escapeHtml(card.image) + '" loading="lazy" alt="' + escapeHtml(card.title) + '">' : '') +
            '<div class="status image-placement-status">' + badgeHtml + '</div>' +
          '</a>' +
        '</div>' +
        '<div class="product-info">' +
          '<div class="product-info-inner">' +
            '<a href="' + escapeHtml(card.url) + '"><span class="prod-title">' + escapeHtml(title) +
              (fabric ? '<br>' + escapeHtml(fabric) : '') + (colour ? '<br>' + escapeHtml(colour) : '') + '</span></a>' +
            '<div class="prod-price">' + priceHtml + '</div>' +
          '</div>' +
          '<div class="list-actions">' +
            sizeHtml +
            '<div class="list-prod-avail list-column">' + launchHtml + messagingHtml + '</div>' +
            '<div class="list-status-notes list-column-double"><div class="list-status">' +
              '<div class="status table-placement list-column">' + badgeHtml + '</div>' +
              '<div class="action list-column">' + addToCartHtml + '</div>' +
            '</div></div>' +
          '</div>' +
          '<div class="prod-item-actions">' +
            buildActionLinksHtml(card, 'wish_list', launchInfo) +
          '</div>' +
        '</div>' +
      '</div>' +
      '</div>'
    );
  }

  // ---------------------------------------------------------------------
  // Add to Cart — self-contained, not reusing the legacy pattern.
  //
  // Checked theme.js before assuming this would "just work": the legacy
  // .AddtoCartListButton handler binds via a static
  // querySelectorAll(...).forEach(...) snapshot, re-run each time the
  // OLD AJAX-fetch-per-item cycle finishes appending cards. Our cards
  // are rendered on a different cycle, so they'd never receive that
  // listener. It also depends on a hidden `<form id="form-{productId}">`
  // the legacy card markup renders that ours doesn't. Rather than
  // reproduce both of those, this uses Shopify's AJAX cart endpoint
  // directly via event delegation (works for any card rendered now or
  // later, no hidden form needed).
  //
  // Drawer opening: traced from theme.js's own cart-trigger click
  // handler (~line 3294) — same functions the header cart icon uses,
  // Shopify.theme.cart.getCart() + Shopify.theme.ajaxCart.updateView()
  // + showDrawer()/showModal(), driven by the theme's own #cart-config.
  // Deliberately NOT replicating the "else" branch of that handler
  // (redirect to the cart page) — that's only appropriate for clicking
  // the cart icon itself, not for an Add to Cart action elsewhere.
  // ---------------------------------------------------------------------
  function openCartDrawerIfApplicable() {
    if (!window.Shopify || !Shopify.theme || !Shopify.theme.cart || !Shopify.theme.ajaxCart) return;
    var configEl = document.getElementById('cart-config');
    var config = {};
    try { config = configEl ? JSON.parse(configEl.innerHTML || '{}') : {}; } catch (e) { /* ignore */ }

    Shopify.theme.cart.getCart().then(function (cart) {
      Shopify.theme.ajaxCart.updateView(config, cart);
      if (config.cart_action === 'drawer') {
        Shopify.theme.ajaxCart.showDrawer(config);
      } else if (config.cart_action === 'modal_cart') {
        Shopify.theme.ajaxCart.showModal(config);
      }
      // Any other cart_action (e.g. full cart page) — no drawer/modal to
      // open, leave as-is rather than redirecting away unexpectedly.
    });
  }

  // Real bug found and fixed: this used to scope to $wishSection only
  // (`if (!$.contains($wishSection, $btn[0])) return;`), which made
  // sense when this was built in Phase 2a, before Reserve List/
  // Recommended existed. Neither of those got their own Add to Cart
  // handler when they reused the same .AddtoCartListButton markup in
  // Phase 2b/2c — clicking it there silently no-op'd (button rendered
  // fine, click handler exited immediately). Add to Cart's behavior is
  // identical regardless of which list a card belongs to (unlike
  // Remove/Move, which legitimately need per-section scoping since
  // their logic differs by list type) — no scoping needed here at all.
  $(document).on('click', '.AddtoCartListButton', function () {
    var $btn = $(this);
    var variantId = $btn.data('productvariantid');
    if (!variantId) return;

    var originalLabel = $btn.val();
    $btn.prop('disabled', true).val('Adding...');

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (body) { throw new Error(body.description || 'Add to cart failed'); });
        return res.json();
      })
      .then(function () {
        $btn.val('Added!');
        openCartDrawerIfApplicable();
        setTimeout(function () { $btn.prop('disabled', false).val(originalLabel); }, 2000);
      })
      .catch(function (err) {
        console.warn('[OmneoLists Cards] Add to cart failed', err);
        $btn.prop('disabled', false).val(originalLabel);
      });
  });

  // ---------------------------------------------------------------------
  // Remove from Wish List — was flagged as a Phase 2d gap, brought
  // forward since it's small and the data attributes/core API already
  // exist. Optimistic removal (card disappears immediately) — if the
  // Omneo delete fails, the next lists:update/reload will show it again
  // rather than silently staying gone with no record.
  // ---------------------------------------------------------------------
  $(document).on('click', '.action-link[data-action="remove-list-item"]', function () {
    var $link = $(this);
    if (!$.contains($wishSection, $link[0])) return;

    var $wrapper = $link.closest('.product-index');
    var $inner = $wrapper.find('.product-index-inner');
    var omneoItemId = $inner.data('omneo-item-id');
    var variantId = $link.data('productvariantid');
    var title = $link.data('producttitle');

    // Loader shows for the actual duration of the background sync, not
    // just a synchronous flash — calling the "loading" and "loaded"
    // functions back-to-back with nothing async between them would be
    // imperceptible to a human. The card itself still disappears
    // immediately (optimistic) — this is about the header loader/count
    // specifically confirming the action is still in progress.
    showWishlistLoadingState();
    $wrapper.remove();

    OmneoLists.remove('wish_list', variantId, omneoItemId || null, { title: title }).then(function () {
      updateMessaging($wishSection.querySelectorAll('.product-index').length);
    });
  });

  // ---------------------------------------------------------------------
  // Move between lists — Phase 2d. Single shared handler for all four
  // move actions found in the legacy card template:
  //   reserved-to-wishlist        (Reserve → Wish List)
  //   wishlist-to-reserve-list    (Wish List → Reserve, level-3-plus)
  //   recommended-to-wishlist     (Recommended → Wish List)
  //   recommended-to-reserve-list (Recommended → Reserve, level-3-plus)
  //
  // Placed here (in the always-present Wish List section) rather than
  // inside any single list's `if ($xSection)` block, since moves are
  // cross-section by nature and need to register regardless of which
  // combination of sections exist on the page.
  //
  // Enforces the one-size-per-product Reserve List rule (Phase 1) when
  // moving INTO Reserve List — checks the cached Omneo lists data for
  // an existing active (not Not-Taken/Archived) reservation of the same
  // product before allowing the move, exactly as flagged as required in
  // §6 when this phase was scoped. Blocks with a message rather than
  // auto-swapping, matching the block-not-swap decision made for the
  // product-page version of this same rule back in Phase 1.
  //
  // ⚠ KNOWN GAP: uses a plain browser alert() for the block message —
  // functional but not polished. No existing inline-message element on
  // the list page fits this cross-section case the way #list-messaging
  // does on the product page. Worth a nicer inline UI later if this
  // comes up often in practice.
  // ---------------------------------------------------------------------
  var MOVE_ACTIONS = {
    'reserved-to-wishlist': { from: 'reserve_list', to: 'wish_list' },
    'wishlist-to-reserve-list': { from: 'wish_list', to: 'reserve_list' },
    'recommended-to-wishlist': { from: 'recommended_list', to: 'wish_list' },
    'recommended-to-reserve-list': { from: 'recommended_list', to: 'reserve_list' }
  };

  function isProductAlreadyReserved(handle) {
    var client = OmneoLists._getClient();
    if (!client || typeof client.find !== 'function') return false;
    var cached = client.find('lists');
    var reserveList = cached && cached.data && cached.data['reserve-list'];
    if (!reserveList || !reserveList.items) return false;

    return Object.values(reserveList.items).some(function (item) {
      var itemHandle = getCleanProductHandle(item.product || {});
      var status = item.status ? item.status.replace(/ /g, '-') : item.status;
      var isResolved = status === 'Not-Taken' || status === 'Archived';
      return itemHandle === handle && !isResolved;
    });
  }

  // Generic, section-agnostic count refresh — used by the move handler
  // below, which can't call updateMessaging()/updateReserveMessaging()/
  // updateRecommendedMessaging() directly since the latter two are
  // function declarations inside their own `if ($xSection)` blocks
  // (block-scoped in strict mode) and this handler lives in the always-
  // present Wish List scope. Found via staging testing: counts weren't
  // updating on move, traced to this scoping gap.
  //
  // Looks up section elements fresh via getElementById() each call,
  // rather than referencing $reserveSection/$recommendedSection
  // directly — those are declared with `var` further down in the file
  // (after this code, in execution order), so capturing them into an
  // object literal here would have captured `undefined` permanently.
  // getElementById() has no such ordering dependency.
  var LIST_SECTION_IDS = {
    wish_list: { id: 'wish-list-section', header: 'wish_list_header', accordion: 'wish_list_accordion' },
    reserve_list: { id: 'reserve-list-section', header: 'reserve_list_header', accordion: 'reserve_list_accordion' },
    recommended_list: { id: 'recommended-list-section', header: 'recommended_list_header', accordion: 'recommended_list_accordion' }
  };

  // Generic loading-state trigger, same reasoning as above — the move handler lives in the always-present Wish List
  // scope and can't reach showReserveLoadingState()/
  // showRecommendedLoadingState() directly (block-scoped inside their
  // own if ($xSection) blocks). Shows immediately on a move click, so
  // there's visible feedback during the gap before the eventual full
  // reload — otherwise that gap has no indication anything is
  // happening.
  var LOADING_MESSAGES = {
    wish_list: 'WISH LIST',
    reserve_list: 'RESERVE LIST'
    // recommended_list deliberately omitted — no existing loader/
    // messaging pattern on page load to reuse there, per request.
  };

  function showSectionLoadingState(type) {
    if (type === 'recommended_list') return;
    var info = LIST_SECTION_IDS[type];
    if (!info) return;
    $('.' + info.header + ' .loader').css('display', '');
    $('.' + info.header + ' .list-product-count').css('display', 'none');
    $('.' + info.accordion + ' .wishlist-messaging .product-count').empty();
    $('.' + info.accordion + ' .wishlist-messaging .messaging-status')
      .html('! Loading your <b>' + (LOADING_MESSAGES[type] || '') + '</b>.');
  }

  $(document).on('click', '.action-link[data-action]', function () {
    var $link = $(this);
    var move = MOVE_ACTIONS[$link.data('action')];
    if (!move) return; // not a move action (e.g. remove-list-item — handled separately, per section)

    var handle = $link.data('producthandle');
    var productId = $link.data('productid');
    var variantId = $link.data('productvariantid');
    var sku = $link.data('productsku');
    var title = $link.data('producttitle');

    if (move.to === 'reserve_list' && isProductAlreadyReserved(handle)) {
      alert('Only one size can be added to your RESERVE LIST. Please manage your existing reservation first.');
      return;
    }

    var $wrapper = $link.closest('.product-index');
    var $inner = $wrapper.find('.product-index-inner');
    var omneoItemId = $inner.data('omneo-item-id');

    // Loader persists until the reload below — no intermediate count
    // refresh here, since showing the loader (which hides the count
    // badge) and then immediately showing it again would conflict
    // visually, and the reload shows final state regardless.
    showSectionLoadingState(move.from);
    showSectionLoadingState(move.to);
    $wrapper.remove();

    // CORRECTED APPROACH — supersedes the v0.9.3 delay-based mitigation,
    // which couldn't actually have worked: staging testing showed the
    // destination list only ever displayed the moved item after a full
    // page reload, no matter how long the delay before refreshLists().
    // That points to the real issue being that shapesClient.hydrate()
    // likely doesn't force a genuinely fresh network fetch on a repeat
    // in-session call for an already-hydrated resource — it probably
    // just returns what's already cached in memory, the same way many
    // SDKs treat hydrate/fetch-once patterns. No amount of waiting
    // fixes that, since there's no fresh data to wait for.
    //
    // Without documentation for this SDK to find a genuine force-
    // refresh method, a full reload is the only currently-confirmed-
    // reliable way to guarantee the destination list shows the moved
    // item. Traded off deliberately: correctness over smoothness for
    // this specific action, confirmed acceptable.
    OmneoLists.add(move.to, variantId, sku, { handle: handle, productId: productId, title: title })
      .then(function () { return OmneoLists.remove(move.from, variantId, omneoItemId || null, { title: title }); })
      .then(function () {
        window.location.reload();
      });
  });

  // ---------------------------------------------------------------------
  // Rendering orchestration
  // ---------------------------------------------------------------------
  function sortCards(cards) {
    return cards.slice().sort(function (a, b) {
      var badgeA = computeBadge(a).sortOrder;
      var badgeB = computeBadge(b).sortOrder;
      if (badgeA !== badgeB) return badgeA - badgeB;
      var secA = parseLaunchInfo(a.tags).sortOrderSecondary;
      var secB = parseLaunchInfo(b.tags).sortOrderSecondary;
      return secB - secA; // most recent launch date first, matching legacy descending intent
    });
  }

  function updateMessaging(count) {
    var isLoggedIn = OmneoLists._getConfig().isLoggedIn;

    // Header title-holder: loader hides once loaded, count badge shows.
    $('.wish_list_header .loader').css('display', 'none');
    $('.wish_list_header .list-product-count')
      .css('display', 'inline-block')
      .find('span').text(count);

    // Toolbar messaging inside the panel.
    $('.wish_list_accordion .wishlist-messaging .product-count').text(count + ' products');
    if (isLoggedIn) {
      $('.wish_list_accordion .wishlist-messaging .messaging-status')
        .html('! Your <b>WISH LIST</b> is saved to your profile.');
    }

    $('#empty-wishlist').toggle(count === 0);
    $('#items-in-wishlist').toggle(count > 0);
  }

  function renderCards(cards) {
    var sorted = sortCards(cards);
    var html = sorted.map(buildCardHtml).join('');
    $wishSection.innerHTML = html;
    updateMessaging(sorted.length);
  }

  // Confirmed matching legacy intent: the old code skipped FETCHING
  // "Expired" wish-list items entirely (see omneo-lists.liquid,
  // moveAlongOmneoWishlist — `if (productStatus == 'Expired') { ...
  // Skipping ... }`), not just labeling them. "Re Stock" items (old +
  // restock-tagged) still show, since the whole point of that status is
  // to keep surfacing items that might come back — only truly
  // abandoned-old, non-restock items are hidden. Per business decision,
  // confirmed 2026-07-01: item.createdAt older than 6 months AND no
  // `restock` tag → don't show at all.
  function isWishlistItemVisible(card) {
    return computeWishlistStatus(card) !== 'Expired';
  }

  // ---------------------------------------------------------------------
  // Progressive rendering — cards appear as each fetch resolves instead
  // of waiting for every single one (Promise.all) before showing
  // anything. With ~15-18 saved items being normal for real accounts,
  // waiting for the slowest of that many concurrent requests before the
  // section shows anything made the page feel inert. Appends cards in
  // arrival order as they resolve (not final sort order — a card can
  // visually land anywhere while fetches are still in flight), then
  // does one final full sorted re-render once everything's settled.
  // Loader/messaging only flips to the "loaded" state on that final
  // pass, not per-card — avoids the header state flickering N times.
  // ---------------------------------------------------------------------
  // Render-generation guard — found via a real bug report: the count
  // badge was visibly changing mid-load. Root cause: this SDK can fire
  // shapes.lists.update more than once during a single page load
  // (already known from elsewhere in this file). Without this guard,
  // a second call to renderProgressive() clears the section and resets
  // its counters immediately, but any STILL-IN-FLIGHT promises from the
  // first call keep resolving in the background — they'd append stale
  // cards into the new round's section, and if the first round's own
  // total happened to complete after the second round started, it
  // would finalize the count/loader with stale, now-superseded data,
  // potentially followed by the second round finishing and overwriting
  // it again. This tracks which render is current and makes any
  // superseded round's completions silently no-op instead of writing.
  //
  // REVEAL DEBOUNCE — a second, related bug: even with the guard above,
  // a genuinely-completed FIRST round (stale pre-move/pre-action cached
  // data) can finish cleanly and correctly reveal its own count, only
  // for a SECOND round (the true, post-action data) to arrive moments
  // later and update it again — visible as "shows 5, then changes to
  // 4". There's no signal from the SDK for "this is truly the last
  // update", so this is a best-effort heuristic, not a guarantee:
  // after a round finishes, wait briefly before revealing; if a newer
  // round starts within that window, the pending reveal is cancelled
  // and the newer round's own (debounced) reveal takes over instead.
  var wishRenderGeneration = 0;
  var wishRevealTimer = null;
  var REVEAL_DEBOUNCE_MS = 500;

  function renderProgressive(cardPromises) {
    var myGeneration = ++wishRenderGeneration;
    if (wishRevealTimer) clearTimeout(wishRevealTimer);
    $wishSection.innerHTML = '';
    var completed = [];
    var settledCount = 0;
    var total = cardPromises.length;

    if (total === 0) {
      if (myGeneration === wishRenderGeneration) {
        wishRevealTimer = setTimeout(function () {
          if (myGeneration === wishRenderGeneration) renderCards([]);
        }, REVEAL_DEBOUNCE_MS);
      }
      return;
    }

    cardPromises.forEach(function (cardPromise) {
      cardPromise.then(function (card) {
        if (myGeneration !== wishRenderGeneration) return; // superseded by a newer render — ignore
        settledCount++;
        if (card) {
          completed.push(card);
          $wishSection.insertAdjacentHTML('beforeend', buildCardHtml(card));
        }
        if (settledCount === total) {
          // Debounced reveal, not immediate — see comment block above.
          if (wishRevealTimer) clearTimeout(wishRevealTimer);
          wishRevealTimer = setTimeout(function () {
            if (myGeneration === wishRenderGeneration) renderCards(completed);
          }, REVEAL_DEBOUNCE_MS);
        }
      });
    });
  }

  function renderFromOmneo(dataObject) {
    var list = dataObject && dataObject['wish-list'];
    if (!list || !list.items) {
      renderCards([]);
      return;
    }
    var stubs = Object.values(list.items).map(normalizeOmneoItem);

    var cardPromises = stubs.map(function (stub) {
      return fetchProductJsonWithFallback('wish_list', stub.handle, stub.sku)
        .then(function (productJson) {
          var card = buildCardFromProductJson(productJson, { sku: stub.sku }, {
            omneoItemId: stub.omneoItemId,
            createdAt: stub.createdAt
          });
          return isWishlistItemVisible(card) ? card : null;
        })
        .catch(function (err) {
          console.warn('[OmneoLists Cards] Failed to fetch product', stub.handle, err);
          if (!isExpectedMissingProduct(err)) OmneoLists.reportRenderFailure('wish_list', stub.handle, err);
          return null;
        });
    });

    renderProgressive(cardPromises);
  }

  function renderFromSession() {
    var entries = OmneoLists.getAllEntries('wish_list');
    if (!entries.length) {
      renderCards([]);
      return;
    }

    var cardPromises = entries.map(function (entry) {
      return fetchProductJson(entry.handle)
        .then(function (productJson) {
          return buildCardFromProductJson(productJson, { variantId: entry.variantId }, {});
        })
        .catch(function (err) {
          console.warn('[OmneoLists Cards] Failed to fetch guest product', entry.handle, err);
          if (!isExpectedMissingProduct(err)) OmneoLists.reportRenderFailure('wish_list', entry.handle, err);
          return null;
        });
    });

    renderProgressive(cardPromises);
  }

  function init() {
    var isLoggedIn = OmneoLists._getConfig().isLoggedIn;
    if (isLoggedIn) {
      OmneoLists.on('lists:ready', renderFromOmneo);
      OmneoLists.on('lists:update', renderFromOmneo);
      // In case lists data is already cached by the time this runs
      var client = OmneoLists._getClient();
      if (client && typeof client.find === 'function') {
        var cached = client.find('lists');
        if (cached && cached.data) renderFromOmneo(cached.data);
      }
    } else {
      renderFromSession();
      OmneoLists.on('session:updated', function (detail) {
        if (detail.type === 'wish_list') renderFromSession();
      });
    }
  }

  // =======================================================================
  // PHASE 2b — Reserve List
  //
  // Reuses computeBadge/parseLaunchInfo/getCleanProductHandle/
  // fetchProductJson/buildCardFromProductJson/money/escapeHtml/
  // renderProgressive-style pattern from the Wish List section above.
  // Logged-in only — no guest path, per business rules (Reserve List
  // requires Level 3/4/Team Fave, always logged in).
  // =======================================================================
  if ($reserveSection) {

    // ---------------------------------------------------------------------
    // Status handling — ported from omneo-lists.liquid's reserve-list
    // status logic (itemData.status.replace(/ /g, "-")) and the
    // fetch-skip check (productStatus == 'Archived' || 'Not-Taken').
    //
    // NOTE: deliberately NOT reusing Phase 1's isReserveItemActive() from
    // omneo-lists-ui.js (product-page lock, with its 7-day null-status
    // grace period) — that's a different concern (blocking the product
    // page) from this (what shows on the list page itself). The legacy
    // list-fetch-skip check only ever matched literal "Archived"/
    // "Not-Taken", never null — so null-status (pending, unprocessed)
    // items DO show on the list page, unlike the product-page lock's
    // grace-period treatment of null.
    // ---------------------------------------------------------------------
    var RESERVE_LIST_STATUSES_TO_HIDE = ['Not-Taken', 'Archived'];

    function normalizeReserveStatus(rawStatus) {
      return rawStatus ? rawStatus.replace(/ /g, '-') : rawStatus;
    }

    // New rule (not in the legacy code, confirmed via business rules
    // doc): "products are hidden from reserve list 7 days after
    // launch". Distinct from the "closed" window (7 days BEFORE launch)
    // parseLaunchInfo() already computes — this is the opposite end of
    // the same date.
    function isPastPostLaunchWindow(launchInfo) {
      return launchInfo.launchDate && launchInfo.daysToLaunch !== null && launchInfo.daysToLaunch <= -7;
    }

    function isReserveItemVisible(status, launchInfo) {
      if (RESERVE_LIST_STATUSES_TO_HIDE.indexOf(status) !== -1) return false;
      if (isPastPostLaunchWindow(launchInfo)) return false;
      return true;
    }

    function normalizeReserveOmneoItem(item) {
      var variant = item.product_variant || {};
      var product = item.product || {};
      var handle = getCleanProductHandle(product);
      return {
        omneoItemId: item.id,
        handle: handle,
        sku: variant.sku,
        status: normalizeReserveStatus(item.status),
        createdAt: item.created_at ? new Date(item.created_at.replace(' ', 'T') + 'Z') : null
      };
    }

    // ---------------------------------------------------------------------
    // Card markup — mirrors buildCardHtml (Wish List) with Reserve-List-
    // specific additions: status-based CSS class, reserved-state
    // overlay messaging (available vs not-available), and no size-badge
    // duplication logic changes needed since computeBadge/parseLaunchInfo
    // are fully shared.
    // ---------------------------------------------------------------------
    function buildReserveCardHtml(card) {
      var badge = computeBadge(card);
      var launchInfo = parseLaunchInfo(card.tags);

      var title = card.title;
      var colour = '';
      var fabric = '';
      if (card.title && card.title.indexOf('(') !== -1) {
        title = card.title.split('(')[0];
        var afterOpen = card.title.split('(').slice(1).join('(');
        fabric = afterOpen.split(')')[0];
        colour = afterOpen.split(')').slice(1).join(')');
      }

      var priceHtml = card.compareAtPrice
        ? '<span style="text-decoration: line-through; opacity: 0.5;">' + money(card.compareAtPrice) + '</span> ' + money(card.price)
        : money(card.price);

      var badgeHtml = badge.label ? '<label class="' + badge.cssClass + ' type-reserved">' + badge.label + '</label>' : '';
      var messagingHtml = badge.small ? '<div class="product-status-grid">' + badge.small + '</div>' : '';

      var launchHtml = '';
      if (launchInfo.launchDate) {
        var launchLabel = launchInfo.launchDate.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' }).toUpperCase();
        launchHtml = '<div class="online-date"><span class="label">Available</span> ' + launchLabel + '</div>';
      }

      var sizeHtml = card.size
        ? '<div class="list-prod-size list-column">Size - <span class="large">' + escapeHtml(card.size) + '</span></div>'
        : '';

      // Reserved-state messaging — ported from the legacy card's hidden-
      // by-default paragraphs, shown here directly based on availability
      // rather than toggled via JS after the fact (we already know
      // availability at render time).
      // TEMPORARY, FOR TESTING — visible status label so it's easy to
      // confirm the right cards show/hide/message correctly against
      // real Omneo data. Remove once this is confirmed working.

      // Confirmed condition: only status "Allocated" shows this
      // messaging at all. When the product has a launch date, it must
      // also be within 7 days AFTER launch — the same window
      // isPastPostLaunchWindow() uses to hide the item entirely once
      // it's over (daysToLaunch <= -7), so this messaging is exactly
      // the window between "just launched" and "hidden from the list".
      // Products with no launch tag (evergreen, always-available items)
      // have no launch date to gate against — status alone applies.
      var showReservedMessage = card.status === 'Allocated' &&
        (!launchInfo.launchDate || (launchInfo.daysToLaunch <= 0 && launchInfo.daysToLaunch > -7));

      var reservedMessageHtml = '';
      if (showReservedMessage) {
        reservedMessageHtml = card.available
          ? '<p class="type-reserved">Your requested size is now \u2018Reserved For You\u2019 at your preferred store. Please complete your purchase within 5 days.</p>'
          : '<p class="type-reserved">We are sorry you missed out. If/when your size becomes available we\u2019ll reserve it for you.</p>';
      }

      var isComingSoon = card.tags.indexOf('coming-soon') !== -1;
      var isEarlyAccess = card.tags.indexOf('early-access') !== -1;
      var isLocked = card.tags.indexOf('manual-list-lock') !== -1;
      var showAddToCart = card.available && !isLocked && (!isComingSoon || isEarlyAccess);

      var addToCartHtml = '';
      if (showAddToCart && card.variantId) {
        addToCartHtml =
          '<div class="wishlist-form-add-to-cart-wrap">' +
            '<input type="button" class="AddtoCartListButton button" ' +
              'data-producthandle="' + escapeHtml(card.handle) + '" data-productid="' + card.productId +
              '" data-productvariantid="' + card.variantId + '" value="Add to Cart" />' +
          '</div>';
      }

      var idAttr = card.omneoItemId
        ? 'omneo-' + card.omneoItemId
        : card.productId + '-' + (card.variantId || '');
      var statusClass = card.status ? ' status-' + card.status : '';

      return (
        '<div class="product-index wishlist-list-item span-3 sm-span-12 auto session-item">' +
        '<div id="' + idAttr + '" class="product-index-inner' + statusClass + '"' +
          ' data-handle="' + escapeHtml(card.handle) + '" data-sku="' + escapeHtml(card.sku) + '"' +
          ' data-sortorder="' + badge.sortOrder + '" data-sortordersecondary="' + launchInfo.sortOrderSecondary + '"' +
          (card.omneoItemId ? ' data-omneo-item-id="' + card.omneoItemId + '"' : '') + '>' +
          '<div class="js-product-image prod-image">' +
            '<a href="' + escapeHtml(card.url) + '" title="' + escapeHtml(card.title) + '">' +
              (card.image ? '<img src="' + escapeHtml(card.image) + '" loading="lazy" alt="' + escapeHtml(card.title) + '">' : '') +
              '<div class="status image-placement-status">' + badgeHtml + '</div>' +
            '</a>' +
          '</div>' +
          '<div class="product-info">' +
            '<div class="product-info-inner">' +
              '<a href="' + escapeHtml(card.url) + '"><span class="prod-title">' + escapeHtml(title) +
                (fabric ? '<br>' + escapeHtml(fabric) : '') + (colour ? '<br>' + escapeHtml(colour) : '') + '</span></a>' +
              '<div class="prod-price">' + priceHtml + '</div>' +
            '</div>' +
            '<div class="list-actions">' +
              sizeHtml +
              '<div class="list-prod-avail list-column">' + launchHtml + messagingHtml + '</div>' +
              '<div class="list-status-notes list-column-double"><div class="list-status">' +
                '<div class="status table-placement list-column">' + badgeHtml + '</div>' +
                '<div class="action list-column">' + addToCartHtml + reservedMessageHtml + '</div>' +
              '</div></div>' +
            '</div>' +
            '<div class="prod-item-actions">' +
              buildActionLinksHtml(card, 'reserve_list', launchInfo) +
            '</div>' +
          '</div>' +
        '</div>' +
        '</div>'
      );
    }

    function sortReserveCards(cards) {
      return cards.slice().sort(function (a, b) {
        var badgeA = computeBadge(a).sortOrder;
        var badgeB = computeBadge(b).sortOrder;
        if (badgeA !== badgeB) return badgeA - badgeB;
        var secA = parseLaunchInfo(a.tags).sortOrderSecondary;
        var secB = parseLaunchInfo(b.tags).sortOrderSecondary;
        return secB - secA;
      });
    }

    function updateReserveMessaging(count) {
      $('.reserve_list_header .loader').css('display', 'none');
      $('.reserve_list_header .list-product-count')
        .css('display', 'inline-block')
        .find('span').text(count);
      $('.reserve_list_accordion .wishlist-messaging .product-count').text(count + ' products');
      $('.reserve_list_accordion .wishlist-messaging .messaging-status')
        .html('! Your <b>RESERVE LIST</b> is saved to your profile.');
    }

    function showReserveLoadingState() {
      $('.reserve_list_header .loader').css('display', '');
      $('.reserve_list_header .list-product-count').css('display', 'none');
      $('.reserve_list_accordion .wishlist-messaging .product-count').empty();
      $('.reserve_list_accordion .wishlist-messaging .messaging-status')
        .html('! Loading your <b>RESERVE LIST</b>.');
    }

    function renderReserveCards(cards) {
      var sorted = sortReserveCards(cards);
      $reserveSection.innerHTML = sorted.map(buildReserveCardHtml).join('');
      updateReserveMessaging(sorted.length);
    }

    // Same generation guard as Wish List's renderProgressive — see that
    // function's comment for the full reasoning.
    // Same generation guard AND reveal debounce as Wish List's
    // renderProgressive — see that function's comments for the full
    // reasoning.
    var reserveRenderGeneration = 0;
    var reserveRevealTimer = null;

    function renderReserveProgressive(cardPromises) {
      var myGeneration = ++reserveRenderGeneration;
      if (reserveRevealTimer) clearTimeout(reserveRevealTimer);
      $reserveSection.innerHTML = '';
      var completed = [];
      var settledCount = 0;
      var total = cardPromises.length;

      if (total === 0) {
        if (myGeneration === reserveRenderGeneration) {
          reserveRevealTimer = setTimeout(function () {
            if (myGeneration === reserveRenderGeneration) renderReserveCards([]);
          }, REVEAL_DEBOUNCE_MS);
        }
        return;
      }

      cardPromises.forEach(function (cardPromise) {
        cardPromise.then(function (card) {
          if (myGeneration !== reserveRenderGeneration) return;
          settledCount++;
          if (card) {
            completed.push(card);
            $reserveSection.insertAdjacentHTML('beforeend', buildReserveCardHtml(card));
          }
          if (settledCount === total) {
            if (reserveRevealTimer) clearTimeout(reserveRevealTimer);
            reserveRevealTimer = setTimeout(function () {
              if (myGeneration === reserveRenderGeneration) renderReserveCards(completed);
            }, REVEAL_DEBOUNCE_MS);
          }
        });
      });
    }

    function renderReserveFromOmneo(dataObject) {
      var list = dataObject && dataObject['reserve-list'];
      if (!list || !list.items) {
        renderReserveCards([]);
        return;
      }
      var stubs = Object.values(list.items).map(normalizeReserveOmneoItem);

      var cardPromises = stubs.map(function (stub) {
        return fetchProductJsonWithFallback('reserve_list', stub.handle, stub.sku)
          .then(function (productJson) {
            var card = buildCardFromProductJson(productJson, { sku: stub.sku }, {
              omneoItemId: stub.omneoItemId,
              createdAt: stub.createdAt
            });
            card.status = stub.status;
            var launchInfo = parseLaunchInfo(card.tags);
            return isReserveItemVisible(stub.status, launchInfo) ? card : null;
          })
          .catch(function (err) {
            console.warn('[OmneoLists Cards] Failed to fetch reserve product', stub.handle, err);
            if (!isExpectedMissingProduct(err)) OmneoLists.reportRenderFailure('reserve_list', stub.handle, err);
            return null;
          });
      });

      renderReserveProgressive(cardPromises);
    }

    // ---------------------------------------------------------------------
    // Preferred store display — from the Omneo profile payload's
    // preferred_location (already fetched by omneo-lists.js; confirmed
    // shape via the real /apps/cx response examined during Phase 1
    // testing: profile.preferred_location.{name, phone}).
    // ---------------------------------------------------------------------
    function updatePreferredStore(profile) {
      var location = profile && profile.preferred_location;
      if (!location) return;
      $('#accordion_list_reserve_list_section .your-preferred-store .store-name').text(location.name || '');
      $('#accordion_list_reserve_list_section .your-preferred-store .store-phone').text(location.phone || '');
    }
    OmneoLists.on('profile:update', updatePreferredStore);

    // Remove — same pattern as Wish List's, scoped to $reserveSection.
    $(document).on('click', '.action-link[data-action="remove-list-item"]', function () {
      var $link = $(this);
      if (!$.contains($reserveSection, $link[0])) return;

      var $wrapper = $link.closest('.product-index');
      var $inner = $wrapper.find('.product-index-inner');
      var omneoItemId = $inner.data('omneo-item-id');
      var variantId = $link.data('productvariantid');
      var title = $link.data('producttitle');

      showReserveLoadingState();
      $wrapper.remove();

      OmneoLists.remove('reserve_list', variantId, omneoItemId || null, { title: title }).then(function () {
        updateReserveMessaging($reserveSection.querySelectorAll('.product-index').length);
      });
    });

    // Reserve List is logged-in only — no guest branch needed.
    OmneoLists.on('lists:ready', renderReserveFromOmneo);
    OmneoLists.on('lists:update', renderReserveFromOmneo);
    var reserveClient = OmneoLists._getClient();
    if (reserveClient && typeof reserveClient.find === 'function') {
      var reserveCached = reserveClient.find('lists');
      if (reserveCached && reserveCached.data) renderReserveFromOmneo(reserveCached.data);
    }

    // Hook the shared refresh button into the Reserve List loading state
    // too — initRefreshList() (below) only knew about Wish List when it
    // was written; extending here keeps that function itself unchanged.
    $(document).on('click', '.refresh-view a', function () {
      if (OmneoLists._getConfig().isLoggedIn) showReserveLoadingState();
    });
  }

  // ---------------------------------------------------------------------
  // Grid/list view toggle — brought in-house rather than relying on the
  // legacy handler, which lives deeply nested inside the old
  // ViewedList session-rendering init sequence in theme.js (after a
  // try/catch around old reserve-list setup) and wasn't reliably
  // registering. Confirmed via staging testing: clicking the toggle
  // icons never changed the grid-product-view/list-product-view class
  // at all — not a CSS issue, the class itself never changed, pointing
  // to the click handler not running.
  //
  // Same class names, same cookie key ('list-layout-view'), same
  // default-source (data-grid-type-default on .list-page.accordion__section)
  // as the legacy version — existing CSS (.list-product-view .span-3.auto
  // etc.) and any other code reading that cookie keep working unchanged.
  // ---------------------------------------------------------------------
  function applyListLayoutView(view) {
    var $panels = $('.c-accordion__panel').find('.collection__page-products');
    $panels.removeClass('grid-product-view list-product-view').addClass(view + '-product-view');
    $('.grid-layout-view-icons .list-view-icon').removeClass('current');
    $('.grid-layout-view-icons .list-view-icon.' + view + '-view').addClass('current');
  }

  function initViewToggle() {
    var savedView = window.Cookies ? window.Cookies.get('list-layout-view') : null;
    var defaultView = $('.list-page.accordion__section').data('grid-type-default') || 'grid';
    applyListLayoutView(savedView || defaultView);

    $('.list-page.accordion__section .grid-layout-view-icons').on('click', '.list-view-icon', function () {
      var view = $(this).data('grid-type'); // 'grid' or 'list'
      if (window.Cookies) window.Cookies.set('list-layout-view', view, { expires: 90 });
      applyListLayoutView(view);
    });
  }

  // ---------------------------------------------------------------------
  // "Refresh list" — replaces the old behaviour (an inline IIFE in
  // main-list.liquid that cleared BOTH the wish_list and reserve_list
  // session cookies whenever ?refresh= was present in the URL).
  //
  // That was destructive for guests — the cookie is their only copy of
  // their list, clearing it deletes it with no recovery. For logged-in
  // users it also didn't do what it was meant to: clearing a local
  // cookie mirror doesn't force a fresh pull from Omneo, it just wipes
  // something that gets rebuilt moments later anyway.
  //
  // New behaviour: logged-in → force a real re-fetch via
  // shapesClient.hydrate('lists'). This refreshes ALL three lists at
  // once (wish-list, reserve-list, recommended-for-you-list share the
  // same Shapes 'lists' resource) — including Reserve List/Recommended,
  // which are still rendered by the legacy system but listen to the
  // same shapes.lists.update event, so they benefit too even though
  // Phase 2b/2c haven't replaced them yet. Guest → no-op, nothing to
  // refresh from, no cookie clearing.
  //
  // The corresponding inline script in main-list.liquid should be
  // removed/neutered to fully close off the old destructive path (in
  // case JS fails to load and the link's href falls back to a normal
  // navigation) — see docs/omneo-lists-README.md for exact instructions.
  // ---------------------------------------------------------------------
  // Mirrors updateMessaging()'s "loaded" state in reverse — shown
  // immediately on refresh click so the customer gets feedback that
  // something is happening, rather than the section looking inert
  // until data happens to finish loading. updateMessaging() flips this
  // back to the loaded state once the re-render completes.
  function showWishlistLoadingState() {
    $('.wish_list_header .loader').css('display', '');
    $('.wish_list_header .list-product-count').css('display', 'none');
    $('.wish_list_accordion .wishlist-messaging .product-count').empty();
    $('.wish_list_accordion .wishlist-messaging .messaging-status')
      .html('! Loading your <b>WISH LIST</b>.');
  }

  function initRefreshList() {
    var isLoggedIn = OmneoLists._getConfig().isLoggedIn;

    // Hide for Recommended For You specifically, always — refreshing
    // doesn't fit that list the same way (staff-managed, not something
    // the customer actively adds/removes to in real time), per request.
    $('#accordion_list_recommended_section .refresh-view').hide();

    // Hide entirely for guests — there's nothing for them to refresh
    // (no Omneo profile), and Reserve List/Recommended are logged-in-
    // only anyway per business rules, so this covers Wish List's and
    // Reserve List's refresh links for guests too.
    if (!isLoggedIn) {
      $('.refresh-view').hide();
      return;
    }

    $(document).on('click', '.refresh-view a', function (e) {
      e.preventDefault();
      showWishlistLoadingState();
      OmneoLists.refreshLists();
    });
  }

  initViewToggle();
  initRefreshList();
  init();

  // =======================================================================
  // PHASE 2c — Recommended For You List
  //
  // Reuses computeBadge/parseLaunchInfo/getCleanProductHandle/
  // fetchProductJson/buildCardFromProductJson/money/escapeHtml from the
  // Wish List section. Logged-in only, same as Reserve List.
  //
  // The core new piece here isn't item rendering (that's the same
  // pattern as the other two lists) — it's LIST-LEVEL visibility
  // gating, which doesn't exist for Wish List or Reserve List at all.
  // Traced precisely from the legacy code (omneo-lists.liquid, the
  // `recommended_list` branch inside listsHandler, ~lines 1416-1550)
  // rather than assumed by analogy to Reserve List's custom_fields
  // (which looked similar but is a different list) — confirmed exact
  // field names, the un-intuitive detail that the "last sent" date uses
  // last_sent's `updated_at` metadata (not its `.value`), and that the
  // "hide when not sent" behaviour is fully CSS-driven via a `.not-sent`
  // class (confirmed in custom.css ~line 7226) — the equivalent JS in
  // the legacy code that would have hidden the section directly is
  // dead/commented out, so toggling the class is sufficient and
  // complete on its own.
  // =======================================================================
  var $recommendedSection = document.getElementById('recommended-list-section');
  if ($recommendedSection) {

    function buildRecommendedCardHtml(card) {
      var badge = computeBadge(card);
      var launchInfo = parseLaunchInfo(card.tags);

      var title = card.title;
      var colour = '';
      var fabric = '';
      if (card.title && card.title.indexOf('(') !== -1) {
        title = card.title.split('(')[0];
        var afterOpen = card.title.split('(').slice(1).join('(');
        fabric = afterOpen.split(')')[0];
        colour = afterOpen.split(')').slice(1).join(')');
      }

      var priceHtml = card.compareAtPrice
        ? '<span style="text-decoration: line-through; opacity: 0.5;">' + money(card.compareAtPrice) + '</span> ' + money(card.price)
        : money(card.price);

      var badgeHtml = badge.label ? '<label class="' + badge.cssClass + ' type-recommended">' + badge.label + '</label>' : '';
      var messagingHtml = badge.small ? '<div class="product-status-grid">' + badge.small + '</div>' : '';

      var launchHtml = '';
      if (launchInfo.launchDate) {
        var launchLabel = launchInfo.launchDate.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' }).toUpperCase();
        launchHtml = '<div class="online-date"><span class="label">Available</span> ' + launchLabel + '</div>';
      }

      var sizeHtml = card.size
        ? '<div class="list-prod-size list-column">Size - <span class="large">' + escapeHtml(card.size) + '</span></div>'
        : '';

      var isComingSoon = card.tags.indexOf('coming-soon') !== -1;
      var isEarlyAccess = card.tags.indexOf('early-access') !== -1;
      var isLocked = card.tags.indexOf('manual-list-lock') !== -1;
      var showAddToCart = card.available && !isLocked && (!isComingSoon || isEarlyAccess);

      var addToCartHtml = '';
      if (showAddToCart && card.variantId) {
        addToCartHtml =
          '<div class="wishlist-form-add-to-cart-wrap">' +
            '<input type="button" class="AddtoCartListButton button" ' +
              'data-producthandle="' + escapeHtml(card.handle) + '" data-productid="' + card.productId +
              '" data-productvariantid="' + card.variantId + '" value="Add to Cart" />' +
          '</div>';
      }

      var idAttr = card.omneoItemId
        ? 'omneo-' + card.omneoItemId
        : card.productId + '-' + (card.variantId || '');

      return (
        '<div class="product-index wishlist-list-item span-3 sm-span-12 auto session-item">' +
        '<div id="' + idAttr + '" class="product-index-inner"' +
          ' data-handle="' + escapeHtml(card.handle) + '" data-sku="' + escapeHtml(card.sku) + '"' +
          ' data-sortorder="' + badge.sortOrder + '" data-sortordersecondary="' + launchInfo.sortOrderSecondary + '"' +
          (card.omneoItemId ? ' data-omneo-item-id="' + card.omneoItemId + '"' : '') + '>' +
          '<div class="js-product-image prod-image">' +
            '<a href="' + escapeHtml(card.url) + '" title="' + escapeHtml(card.title) + '">' +
              (card.image ? '<img src="' + escapeHtml(card.image) + '" loading="lazy" alt="' + escapeHtml(card.title) + '">' : '') +
              '<div class="status image-placement-status">' + badgeHtml + '</div>' +
            '</a>' +
          '</div>' +
          '<div class="product-info">' +
            '<div class="product-info-inner">' +
              '<a href="' + escapeHtml(card.url) + '"><span class="prod-title">' + escapeHtml(title) +
                (fabric ? '<br>' + escapeHtml(fabric) : '') + (colour ? '<br>' + escapeHtml(colour) : '') + '</span></a>' +
              '<div class="prod-price">' + priceHtml + '</div>' +
            '</div>' +
            '<div class="list-actions">' +
              sizeHtml +
              '<div class="list-prod-avail list-column">' + launchHtml + messagingHtml + '</div>' +
              '<div class="list-status-notes list-column-double"><div class="list-status">' +
                '<div class="status table-placement list-column">' + badgeHtml + '</div>' +
                '<div class="action list-column">' + addToCartHtml + '</div>' +
              '</div></div>' +
            '</div>' +
            '<div class="prod-item-actions">' +
              buildActionLinksHtml(card, 'recommended_list', launchInfo) +
            '</div>' +
          '</div>' +
        '</div>' +
        '</div>'
      );
    }

    function sortRecommendedCards(cards) {
      return cards.slice().sort(function (a, b) {
        var badgeA = computeBadge(a).sortOrder;
        var badgeB = computeBadge(b).sortOrder;
        if (badgeA !== badgeB) return badgeA - badgeB;
        var secA = parseLaunchInfo(a.tags).sortOrderSecondary;
        var secB = parseLaunchInfo(b.tags).sortOrderSecondary;
        return secB - secA;
      });
    }

    function updateRecommendedMessaging(count) {
      $('.recommended_list_header .loader').css('display', 'none');
      $('.recommended_list_header .list-product-count')
        .css('display', 'inline-block')
        .find('span').text(count);
      $('.recommended_list_accordion .wishlist-messaging .product-count').text(count + ' products');

      var $status = $('.recommended_list_accordion .wishlist-messaging .messaging-status');
      if (count === 0) {
        $status.html('We have no recommendations at this time.');
      } else {
        $status.empty();
      }
    }

    function renderRecommendedCards(cards) {
      var sorted = sortRecommendedCards(cards);
      $recommendedSection.innerHTML = sorted.map(buildRecommendedCardHtml).join('');
      updateRecommendedMessaging(sorted.length);
    }

    // Same generation guard as Wish List's renderProgressive — see that
    // function's comment for the full reasoning.
    var recommendedRenderGeneration = 0;

    function renderRecommendedProgressive(cardPromises) {
      var myGeneration = ++recommendedRenderGeneration;
      $recommendedSection.innerHTML = '';
      var completed = [];
      var settledCount = 0;
      var total = cardPromises.length;

      if (total === 0) {
        if (myGeneration === recommendedRenderGeneration) renderRecommendedCards([]);
        return;
      }

      cardPromises.forEach(function (cardPromise) {
        cardPromise.then(function (card) {
          if (myGeneration !== recommendedRenderGeneration) return;
          settledCount++;
          if (card) {
            completed.push(card);
            $recommendedSection.insertAdjacentHTML('beforeend', buildRecommendedCardHtml(card));
          }
          if (settledCount === total) renderRecommendedCards(completed);
        });
      });
    }

    // ---------------------------------------------------------------------
    // List-level metadata & visibility gating — the actual core of
    // Phase 2c. Ported precisely from omneo-lists.liquid's
    // recommended_list branch (~lines 1416-1550).
    // ---------------------------------------------------------------------
    var RECOMMENDED_LIST_EXPIRY_DAYS = 21; // "3 weeks", matching the legacy code's literal 3*7

    function updateRecommendedListMeta(listObject) {
      var customFields = listObject.custom_fields || [];
      var $header = $('.recommended_list_header');
      var $accordion = $('#accordion_list_recommended_section');

      // List "expires on" display — from the LIST's own updated_at + 21
      // days, shown as " on the DD/MM" in the header. Distinct from the
      // send_to_customer/last_sent gating below, which uses a custom
      // field's own updated_at, not the list's.
      if (listObject.updated_at) {
        var listUpdated = new Date(listObject.updated_at.replace(' ', 'T') + 'Z');
        var expiry = new Date(listUpdated.getTime());
        expiry.setDate(expiry.getDate() + RECOMMENDED_LIST_EXPIRY_DAYS);
        var expiryLabel = String(expiry.getDate()).padStart(2, '0') + '/' + String(expiry.getMonth() + 1).padStart(2, '0');
        $header.find('.dynamicListDate').html(' on the ' + expiryLabel);
      }

      var sendToCustomer = false;
      var signOffText = '';
      var lastSentField = customFields.filter(function (f) {
        return f.namespace === 'clienteling' && f.handle === 'last_sent';
      })[0];
      var sendToCustomerField = customFields.filter(function (f) {
        return f.namespace === 'clienteling' && f.handle === 'send_to_customer';
      })[0];
      var commentField = customFields.filter(function (f) {
        return f.namespace === 'clienteling' && f.handle === 'comment_value';
      })[0];
      var signOffField = customFields.filter(function (f) {
        return f.namespace === 'clienteling' && f.handle === 'omneo_signoff_text';
      })[0];

      if (signOffField && signOffField.value) signOffText = signOffField.value;

      if (sendToCustomerField && lastSentField) {
        // NOTE: uses last_sent's `updated_at` metadata, not its `.value`
        // — matches the legacy code exactly, confirmed by reading it
        // directly rather than assuming .value was intended.
        var sentDate = new Date((lastSentField.updated_at || '').replace(' ', 'T') + 'Z');
        if (!isNaN(sentDate.getTime())) {
          var day = String(sentDate.getDate()).padStart(2, '0');
          var month = String(sentDate.getMonth() + 1).padStart(2, '0');
          var year = String(sentDate.getFullYear()).slice(-2);
          var formattedDate = day + '/' + month + '/' + year;

          var ageMs = Date.now() - sentDate.getTime();
          var expiryMs = RECOMMENDED_LIST_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
          sendToCustomer = ageMs <= expiryMs;

          var suffix = signOffText.trim() ? ' by <strong>' + escapeHtml(signOffText) + '</strong>.' : '.';
          $('#accordion_list_recommended_section .your-preferred-store p.stylists-note-info')
            .html('This list was created on <strong>' + formattedDate + '</strong>' + suffix);
        }
      }

      if (commentField && commentField.value) {
        var limited = commentField.value.length > 500 ? commentField.value.substring(0, 500) : commentField.value;
        var withBreaks = escapeHtml(limited).replace(/\n/g, '<br>');
        $('#accordion_list_recommended_section .your-preferred-store p.stylists-note').html(withBreaks);
      }

      if (sendToCustomer) {
        $('#accordion_list_recommended_section .your-preferred-store').css('display', 'block');
        $header.removeClass('not-sent');
        $accordion.removeClass('not-sent');
      } else {
        $header.addClass('not-sent');
        $accordion.addClass('not-sent');
      }
    }

    function renderRecommendedFromOmneo(dataObject) {
      var list = dataObject && dataObject['recommended-for-you-list'];
      if (!list) {
        renderRecommendedCards([]);
        return;
      }

      updateRecommendedListMeta(list);

      if (!list.items) {
        renderRecommendedCards([]);
        return;
      }
      var stubs = Object.values(list.items).map(function (item) {
        var variant = item.product_variant || {};
        var product = item.product || {};
        return {
          omneoItemId: item.id,
          handle: getCleanProductHandle(product),
          sku: variant.sku,
          createdAt: item.created_at ? new Date(item.created_at.replace(' ', 'T') + 'Z') : null
        };
      });

      var cardPromises = stubs.map(function (stub) {
        return fetchProductJsonWithFallback('recommended_list', stub.handle, stub.sku)
          .then(function (productJson) {
            return buildCardFromProductJson(productJson, { sku: stub.sku }, {
              omneoItemId: stub.omneoItemId,
              createdAt: stub.createdAt
            });
          })
          .catch(function (err) {
            console.warn('[OmneoLists Cards] Failed to fetch recommended product', stub.handle, err);
            if (!isExpectedMissingProduct(err)) OmneoLists.reportRenderFailure('recommended_list', stub.handle, err);
            return null;
          });
      });

      renderRecommendedProgressive(cardPromises);
    }

    // Remove — same pattern as the other two lists, scoped to
    // $recommendedSection. No loading-state call here, unlike wish/
    // reserve — Recommended has no existing loader/messaging pattern
    // on page load to reuse, per request.
    $(document).on('click', '.action-link[data-action="remove-list-item"]', function () {
      var $link = $(this);
      if (!$.contains($recommendedSection, $link[0])) return;

      var $wrapper = $link.closest('.product-index');
      var $inner = $wrapper.find('.product-index-inner');
      var omneoItemId = $inner.data('omneo-item-id');
      var variantId = $link.data('productvariantid');
      var title = $link.data('producttitle');

      $wrapper.remove();
      updateRecommendedMessaging($recommendedSection.querySelectorAll('.product-index').length);

      OmneoLists.remove('recommended_list', variantId, omneoItemId || null, { title: title });
    });

    OmneoLists.on('lists:ready', renderRecommendedFromOmneo);
    OmneoLists.on('lists:update', renderRecommendedFromOmneo);
    var recommendedClient = OmneoLists._getClient();
    if (recommendedClient && typeof recommendedClient.find === 'function') {
      var recommendedCached = recommendedClient.find('lists');
      if (recommendedCached && recommendedCached.data) renderRecommendedFromOmneo(recommendedCached.data);
    }
  }

})(window.jQuery, window.OmneoLists);